import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Search, MapPin, Phone, Mail, Globe, Send, Sparkles, KeyRound, Trash2, Repeat, Target, ExternalLink, Check, List, Map as MapIcon, Inbox, Zap } from "lucide-react";
import LeadMap from "@/components/LeadMap";
import SendingInbox from "@/components/SendingInbox";

const STATUSES = ["new", "contacted", "replied", "won", "lost"];
const STATUS_COLOR = { new: "#00f0ff", contacted: "#ffb454", replied: "#a78bfa", won: "#36f08a", lost: "#f0506e" };

export default function LeadFinder({ onClose, onRefreshUser }) {
  const [connected, setConnected] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [query, setQuery] = useState("");
  const [hunting, setHunting] = useState(false);
  const [leads, setLeads] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [compose, setCompose] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [schedQuery, setSchedQuery] = useState("");
  const [schedCadence, setSchedCadence] = useState("weekly");
  const [view, setView] = useState("list");
  const [source, setSource] = useState("quick");

  const refresh = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.listLeads(), api.leadSchedules()]);
      setLeads(l.leads || []); setSchedules(s.schedules || []);
    } catch {}
  }, []);

  useEffect(() => {
    api.leadSettings().then((s) => setConnected(s.connected)).catch(() => setConnected(false));
    refresh();
  }, [refresh]);

  const saveKey = async () => {
    if (!keyInput.trim()) return toast.error("Paste your Google Places API key");
    setSavingKey(true);
    try { await api.saveLeadKey(keyInput.trim()); setConnected(true); setKeyInput(""); toast.success("Google Places connected!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Key rejected"); }
    finally { setSavingKey(false); }
  };

  const hunt = async () => {
    if (!query.trim()) return toast.error("Try 'dentists in 89109' or 'plumbers in Austin TX'");
    if (source === "pro" && !connected) return toast.error("Connect Google Places for Pro search, or switch to Quick.");
    setHunting(true);
    try {
      const r = await api.leadHunt(query.trim(), 20, true, source);
      toast.success(`Found ${r.created} new lead${r.created === 1 ? "" : "s"}`);
      onRefreshUser?.(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Hunt failed"); }
    finally { setHunting(false); }
  };

  const setStatus = async (lead, status) => {
    setLeads((p) => p.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try { await api.updateLead(lead.id, { status }); } catch { toast.error("Could not update"); }
  };

  const removeLead = async (lead) => {
    setLeads((p) => p.filter((l) => l.id !== lead.id));
    try { await api.deleteLead(lead.id); } catch {}
  };

  const openCompose = async (lead) => {
    if (!lead.email) return toast.error("No email found for this lead. Try another or add one.");
    setCompose({ lead, subject: "", body: "" });
    setDrafting(true);
    try { const d = await api.leadDraft(lead.id, ""); setCompose({ lead, subject: d.subject, body: d.body }); onRefreshUser?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Draft failed"); }
    finally { setDrafting(false); }
  };

  const sendEmail = async () => {
    if (!compose?.subject?.trim() || !compose?.body?.trim()) return toast.error("Add a subject and message");
    setSending(true);
    try {
      await api.leadSend(compose.lead.id, compose.subject, compose.body);
      toast.success(`Emailed ${compose.lead.name}`); setCompose(null); onRefreshUser?.(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const addSchedule = async () => {
    if (!schedQuery.trim()) return toast.error("Enter a search to run automatically");
    try { await api.addLeadSchedule(schedQuery.trim(), schedCadence); setSchedQuery(""); toast.success("Recurring hunt saved"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  if (connected === null) return <div className="h-full grid place-items-center text-zinc-400"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="h-full flex flex-col" data-testid="lead-finder">
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/10">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Target size={18} /> Lead Finder</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1" data-testid="lead-view-toggle">
            {[["list", List, "List"], ["map", MapIcon, "Map"], ["inbox", Inbox, "Sending Inbox"]].map(([k, Icon, label]) => (
              <button key={k} data-testid={`lead-view-${k}`} onClick={() => setView(k)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-tactical font-bold uppercase transition-colors ${view === k ? "bg-cyan-500/20 text-cyan-200" : "text-zinc-500 hover:text-zinc-300"}`}><Icon size={13} /> <span className="hidden sm:inline">{label}</span></button>
            ))}
          </div>
          <button data-testid="lead-close" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll px-5 py-5">
        {view === "inbox" ? (
          <SendingInbox onClose={() => setView("list")} />
        ) : (
          <div className="max-w-5xl">
            {/* Source selector — Quick (keyless) vs Pro (Google Places) */}
            <div className="flex items-center gap-2 mb-3" data-testid="lead-source">
              <button data-testid="source-quick" onClick={() => setSource("quick")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-tactical font-bold uppercase border transition-colors ${source === "quick" ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}><Zap size={13} /> Quick · no setup</button>
              <button data-testid="source-pro" onClick={() => setSource("pro")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-tactical font-bold uppercase border transition-colors ${source === "pro" ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}><Sparkles size={13} /> Pro · Google {connected && <Check size={12} className="text-emerald-400" />}</button>
              <span className="text-[11px] text-zinc-500 font-mono hidden sm:inline">{source === "quick" ? "Free OpenStreetMap data — instant, zero setup" : "Google Places — richer data & ratings"}</span>
            </div>

            {/* Hunt bar */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input data-testid="lead-query" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && hunt()} placeholder="e.g. dentists in 89109, roofers in Miami FL, gyms near 30303" className="w-full bg-[#0b0e14] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
              </div>
              <button data-testid="lead-hunt-btn" onClick={hunt} disabled={hunting} className="h-12 px-5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-50">{hunting ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} />} Find leads · 2 cr</button>
            </div>

            {/* Pro upgrade — optional Google Places connect, shown only when Pro picked & not connected */}
            {source === "pro" && !connected && (
              <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/[0.05] p-4 mb-4" data-testid="lead-connect">
                <div className="flex items-center gap-2 text-cyan-200 font-tactical font-bold text-sm mb-1.5"><KeyRound size={15} /> Connect Google Places (optional)</div>
                <p className="text-[12px] text-zinc-400 mb-3">Quick search already works free with no setup. Pro adds Google's richer data (ratings, more phones). Paste a Google Places key to enable it.</p>
                <div className="flex gap-2">
                  <input data-testid="lead-key-input" type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Paste your Google Places API key" className="flex-1 bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
                  <button data-testid="lead-key-save" onClick={saveKey} disabled={savingKey} className="px-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-1">{savingKey ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save</button>
                </div>
                <button onClick={() => setShowSteps((v) => !v)} className="mt-3 text-[12px] font-mono text-cyan-300 hover:underline">{showSteps ? "Hide" : "Show"} step-by-step: how to get a key (free) →</button>
                {showSteps && (
                  <ol className="mt-2 space-y-1.5 text-[12px] text-zinc-300 list-decimal list-inside leading-relaxed" data-testid="lead-steps">
                    <li>Go to <a className="text-cyan-300 underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">console.cloud.google.com</a> → new project.</li>
                    <li>Billing → attach a billing account (free Places credit; set a budget alert).</li>
                    <li>APIs &amp; Services → Library → enable <span className="text-zinc-100">"Places API (New)"</span>.</li>
                    <li>Credentials → Create → API key → copy, then paste above.</li>
                  </ol>
                )}
              </div>
            )}
            {source === "pro" && connected && (
              <button onClick={async () => { await api.deleteLeadKey(); setConnected(false); setSource("quick"); toast("Places key removed"); }} className="text-[11px] font-mono text-zinc-500 hover:text-rose-300 mb-4 inline-flex items-center gap-1"><KeyRound size={11} /> Disconnect Google Places key</button>
            )}

            {view === "map" ? (
              <LeadMap leads={leads} onEmail={openCompose} />
            ) : (
            <>
            {/* Recurring hunts */}
            <div className="rounded-xl border border-violet-400/20 bg-violet-500/[0.05] p-4 mb-5" data-testid="lead-schedules">
              <div className="flex items-center gap-2 text-violet-200 text-xs font-tactical font-bold uppercase tracking-wider mb-2"><Repeat size={14} /> Auto-hunt on a schedule</div>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="sched-query" value={schedQuery} onChange={(e) => setSchedQuery(e.target.value)} placeholder="Search to repeat, e.g. 'med spas in 33139'" className="flex-1 min-w-[220px] bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-violet-400/40" />
                <select data-testid="sched-cadence" value={schedCadence} onChange={(e) => setSchedCadence(e.target.value)} className="bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200"><option value="daily">Daily</option><option value="weekly">Weekly</option></select>
                <button data-testid="sched-add" onClick={addSchedule} className="px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase text-xs">Add</button>
              </div>
              {schedules.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {schedules.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-[13px] bg-white/[0.02] rounded-lg px-3 py-1.5">
                      <span className="text-zinc-300 truncate">{s.query} <span className="text-zinc-600 font-mono">· {s.cadence} · 2 cr/run</span></span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={async () => { await api.toggleLeadSchedule(s.id); refresh(); }} className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${s.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-600/20 text-zinc-500"}`}>{s.enabled ? "On" : "Off"}</button>
                        <button onClick={async () => { await api.deleteLeadSchedule(s.id); refresh(); }} className="text-zinc-500 hover:text-rose-300"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Leads */}
            {leads.length === 0 ? (
              <div className="text-center text-zinc-500 text-sm p-10">No leads yet. Run a search above to fill your pipeline.</div>
            ) : (
              <div className="space-y-2" data-testid="lead-list">
                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{leads.length} leads</div>
                {leads.map((l) => (
                  <div key={l.id} data-testid={`lead-${l.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start justify-between gap-4 hover:border-white/20 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-tactical font-bold text-zinc-100 truncate">{l.name}</span>
                        {l.category && <span className="text-[10px] font-mono uppercase text-zinc-500">{l.category}</span>}
                        {l.rating != null && <span className="text-[10px] font-mono text-amber-300">★ {l.rating} ({l.ratings_count || 0})</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-zinc-400">
                        {l.address && <span className="flex items-center gap-1"><MapPin size={12} /> {l.address}</span>}
                        {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-cyan-300"><Phone size={12} /> {l.phone}</a>}
                        {l.email ? <span className="flex items-center gap-1 text-emerald-300"><Mail size={12} /> {l.email}</span> : <span className="flex items-center gap-1 text-zinc-600"><Mail size={12} /> no email</span>}
                        {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-cyan-300"><Globe size={12} /> site <ExternalLink size={10} /></a>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select data-testid={`lead-status-${l.id}`} value={l.status || "new"} onChange={(e) => setStatus(l, e.target.value)} className="bg-[#0b0e14] border border-white/10 rounded-lg px-2 py-1.5 text-xs font-mono" style={{ color: STATUS_COLOR[l.status || "new"] }}>
                        {STATUSES.map((s) => <option key={s} value={s} className="text-zinc-200">{s}</option>)}
                      </select>
                      <button data-testid={`lead-email-${l.id}`} onClick={() => openCompose(l)} disabled={!l.email} className="h-8 px-3 rounded-lg bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-30 text-xs font-tactical font-bold uppercase flex items-center gap-1"><Sparkles size={12} /> Email</button>
                      <button onClick={() => removeLead(l)} className="h-8 w-8 grid place-items-center rounded-lg bg-white/5 text-zinc-500 hover:text-rose-300"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>

      {/* Compose modal */}
      {compose && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !sending && setCompose(null)}>
          <div data-testid="lead-compose" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0b0e14] p-5 anim-slidein">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-tactical font-bold uppercase text-sm text-cyan-300">Email {compose.lead.name}</h3>
              <button onClick={() => setCompose(null)} className="text-zinc-400 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="text-[12px] text-zinc-500 mb-2 flex items-center gap-1"><Mail size={12} /> {compose.lead.email}</div>
            {drafting ? (
              <div className="py-8 grid place-items-center text-violet-300 font-mono text-sm gap-2"><Loader2 className="animate-spin" /> Writing a cold email…</div>
            ) : (
              <>
                <input data-testid="lead-subject" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} placeholder="Subject" className="w-full bg-[#05070b] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 mb-2 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
                <textarea data-testid="lead-body" rows={9} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} placeholder="Your message…" className="w-full bg-[#05070b] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
                <div className="flex justify-end mt-3">
                  <button data-testid="lead-send" onClick={sendEmail} disabled={sending} className="flex items-center gap-1.5 text-xs font-tactical font-bold uppercase bg-cyan-500 hover:bg-cyan-400 text-[#04070c] rounded-lg px-4 py-2 disabled:opacity-50">{sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send · 2 cr</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
